import { get, isNil } from "lodash";
import { SUR_COMPLEMENT_WEIGHT } from "utils/constants";

export const UPSELL_WEIGHT = 1;
export const PACK_WEIGHT = SUR_COMPLEMENT_WEIGHT;

const DEFAULT_PACK_SEGMENT = "P0";
const DEFAULT_RENFORT_SEGMENT = "R0";

const getUpsellPlan = (upsell) => get(upsell, "insurancePlan") || upsell || {};

const resolveCombinationSegment = (plan) =>
  String(get(plan, "alias") || "").trim();

const resolvePackSegment = (packUpsell) => {
  if (isNil(packUpsell)) return DEFAULT_PACK_SEGMENT;

  const segment = resolveCombinationSegment(getUpsellPlan(packUpsell));
  if (!segment) {
    throw new Error(
      "Unable to resolve ACORIS combination code: pack alias is required",
    );
  }
  return segment;
};

const resolveRenfortSegment = (renfortUpsell) => {
  if (isNil(renfortUpsell)) return DEFAULT_RENFORT_SEGMENT;

  const segment = resolveCombinationSegment(getUpsellPlan(renfortUpsell));
  if (!segment) {
    throw new Error(
      "Unable to resolve ACORIS combination code: renfort alias is required",
    );
  }
  return segment;
};

/**
 * Builds the ASSIA code from plan aliases: `<socle><pack><renfort>`.
 * Missing pack / renfort → P0 / R0. Admin sets alias on each plan
 * (e.g. PLE1, P1, R1) because identifier must stay unique.
 */
export const resolveAcorisCombinationCode = ({
  socleInsurancePlan,
  upsells = [],
} = {}) => {
  const socleSegment = resolveCombinationSegment(socleInsurancePlan);
  if (!socleSegment) {
    throw new Error(
      "Unable to resolve ACORIS combination code: socle alias is required",
    );
  }

  const modules = Array.isArray(upsells) ? upsells : [];
  const packUpsell = modules.find(
    (module) => get(module, "weight") === PACK_WEIGHT,
  );
  const renfortUpsell = modules.find(
    (module) => get(module, "weight") === UPSELL_WEIGHT,
  );

  return `${socleSegment}${resolvePackSegment(
    packUpsell,
  )}${resolveRenfortSegment(renfortUpsell)}`;
};

export const resolveProjectCombinationCode = ({
  socleInsurancePlan,
  project,
  socleInsurancePlanId,
}) => {
  const planId = socleInsurancePlanId || get(socleInsurancePlan, "id");
  const upsells = get(project, `fields.upsells.${planId}`, []);

  try {
    return resolveAcorisCombinationCode({
      socleInsurancePlan,
      upsells,
    });
  } catch {
    return null;
  }
};

export const buildCombinationAdditionalInfo = ({
  socleInsurancePlan,
  project,
  socleInsurancePlanId,
  existingAdditionalInfo = {},
}) => {
  const codeCombinaison = resolveProjectCombinationCode({
    socleInsurancePlan,
    project,
    socleInsurancePlanId,
  });

  if (!codeCombinaison) return existingAdditionalInfo;

  return {
    ...existingAdditionalInfo,
    codeCombinaison,
  };
};

export const hasCombinationCodeChanged = (
  additionalInfo,
  existingAdditionalInfo,
) => {
  const nextCode = get(additionalInfo, "codeCombinaison");
  if (!nextCode) return false;
  return nextCode !== get(existingAdditionalInfo, "codeCombinaison");
};

export default resolveAcorisCombinationCode;
